import { toScorecardDataType } from '@/features/ogsm/target-format'
import type { OgsmBoard, OgsmBoardNode, OgsmSession } from '@/types/api'

/** Today in the user's calendar, as the `YYYY-MM-DD` the API takes. */
export function todayIsoDate(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/**
 * A numeric goal linked to a metric that another source feeds. The backend
 * refuses actuals for it while the session still takes readings, so the cell
 * says so instead of just offering nothing.
 */
export function isFedBySource(
  node: Pick<OgsmBoardNode, 'metricLabelId' | 'actualEditable' | 'targetType'>,
  session: Pick<OgsmSession, 'readingsEditable'>
): boolean {
  return (
    Boolean(node.metricLabelId) &&
    !node.actualEditable &&
    session.readingsEditable &&
    toScorecardDataType(node.targetType) !== undefined
  )
}

export interface ActualEntryRow {
  node: OgsmBoardNode
  /** Root-first ancestors, the node itself excluded; [] for a top-level objective. */
  ancestors: OgsmBoardNode[]
  /** The row's top-level objective: `ancestors[0]`, or the node itself at the root. Starts a visual group. */
  rootId: string
}

/** Every node the backend lets take an actual (`actualEditable`), depth-first in board order. */
export function actualEntryRows(board: OgsmBoard): ActualEntryRow[] {
  const rows: ActualEntryRow[] = []
  const walk = (nodes: OgsmBoardNode[], ancestors: OgsmBoardNode[]) => {
    for (const node of nodes) {
      if (node.actualEditable)
        rows.push({ node, ancestors, rootId: ancestors[0]?.id ?? node.id })
      walk(node.strategies, [...ancestors, node])
    }
  }
  walk(board.objectives, [])
  return rows
}

export interface ActualDraft {
  value: number // NaN = empty
  asOfDate: string // YYYY-MM-DD
}

/**
 * Whether a row should be posted: a finite value, changed from what the row was
 * pre-filled with, and not identical to the latest actual already stored.
 */
export function shouldSaveActual(
  latest: OgsmBoardNode['latestActual'],
  prefill: ActualDraft,
  next: ActualDraft
): boolean {
  return (
    Number.isFinite(next.value) &&
    (next.value !== prefill.value || next.asOfDate !== prefill.asOfDate) &&
    !(
      latest != null &&
      latest.value === next.value &&
      latest.asOfDate === next.asOfDate
    )
  )
}
